import { useState, useCallback, useRef, useEffect } from 'react';

const INFERNO_DROP_STRATEGEM_ID = 319;
const NAPALM_DURATION_MS = 2200;

/**
 * Hook to trigger napalm animation over all 4 zones when Inferno Drop (strategem 319) is played.
 * Inferno Drop has no targeting, so it is executed immediately and never sends strategem-initiated.
 * We detect it from gameState when the strategem appears in completedOrders (same pattern as 113 shockwave).
 * @param {Object} gameState - Current game state
 * @param {string} userId - Current user's ID (for skip-first-render)
 * @returns {{ activeNapalms: Array<{ key: string }>, triggerInfernoDrop: (key: string) => void, completeNapalm: (key: string) => void }}
 */
export const useInfernoDropNapalm = (gameState, userId) => {
  const [activeNapalms, setActiveNapalms] = useState([]);
  const processedRef = useRef(new Set());
  const isFirstRenderRef = useRef(true);

  const triggerInfernoDrop = useCallback((key) => {
    if (!key) return;
    setActiveNapalms((prev) => [...prev, { key }]);
    setTimeout(() => {
      setActiveNapalms((prev) => prev.filter((n) => n.key !== key));
    }, NAPALM_DURATION_MS);
  }, []);

  const completeNapalm = useCallback((key) => {
    setActiveNapalms((prev) => prev.filter((n) => n.key !== key));
  }, []);

  useEffect(() => {
    if (!gameState?.state || !userId) return;
    if (isFirstRenderRef.current) {
      isFirstRenderRef.current = false;
      return;
    }

    const allPlayerIds = Object.keys(gameState.state || {});

    allPlayerIds.forEach((playerId) => {
      const player = gameState.state[playerId];
      if (!player?.completedOrders) return;

      player.completedOrders.forEach((strategem) => {
        if (!strategem || Number(strategem.id) !== INFERNO_DROP_STRATEGEM_ID) return;

        const strategemKey = `strategem-319-${strategem.instanceId}-${playerId}`;
        if (processedRef.current.has(strategemKey)) return;

        processedRef.current.add(strategemKey);

        const animationKey = `inferno-319-${strategem.instanceId}-${Date.now()}`;
        triggerInfernoDrop(animationKey);
      });
    });

    if (processedRef.current.size > 50) {
      const keys = Array.from(processedRef.current);
      keys.slice(0, keys.length - 50).forEach((k) => processedRef.current.delete(k));
    }
  }, [gameState, userId, triggerInfernoDrop]);

  return {
    activeNapalms,
    triggerInfernoDrop,
    completeNapalm,
    infernoDropStrategemId: INFERNO_DROP_STRATEGEM_ID,
  };
};

export default useInfernoDropNapalm;
